// The account: who you are signed in as, who else you are signed in as, and the
// way out.
//
// Switching WORKSPACES is not here — that is on the workspace name itself (see
// WorkspaceSwitcher), which is where anyone would look for it. This menu is about
// the person, and the thing it has to get right is that a person can be more than
// one of them.
//
// **Signing in as a second address does not sign the first one out.** Better Auth's
// multiSession plugin keeps every identity proved on this device and makes the most
// recent one active; the others stay addressable. Without this menu that was
// indistinguishable from being logged out: signing in to a new workspace as
// travis@knowledgefutures.org made the travis@saltbark.com superadmin rights
// vanish, and nothing on screen said they were one click away.

import { useCallback, useEffect, useRef, useState } from 'react'

import { api } from '../lib/api'
import { useStore } from '../lib/store'
import { useMe } from '../lib/use-me'
import { Icon } from './Icon'

interface DeviceSession {
  token: string
  name: string
  email: string
}

/** The other identities proved on this device. The library owns the cookies; this
 *  only reads the list and asks it to make one of them active. */
async function deviceSessions(): Promise<DeviceSession[]> {
  const res = await fetch('/api/auth/multi-session/list-device-sessions', {
    credentials: 'same-origin',
  })
  if (!res.ok) return []
  const raw = (await res.json()) as unknown
  if (!Array.isArray(raw)) return []
  return raw.flatMap((r) => {
    const row = r as { session?: { token?: string }; user?: { name?: string; email?: string } }
    const token = row.session?.token
    if (!token) return []
    return [{ token, name: row.user?.name ?? '', email: (row.user?.email ?? '').toLowerCase() }]
  })
}

export function WorkspaceMenu() {
  const { me } = useStore()
  const info = useMe()
  const [open, setOpen] = useState(false)
  const [others, setOthers] = useState<DeviceSession[]>([])
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  const load = useCallback(() => {
    const here = (info?.me.email ?? '').toLowerCase()
    deviceSessions()
      .then((list) => setOthers(list.filter((s) => !here || s.email !== here)))
      .catch(() => undefined)
  }, [info?.me.email])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  /** Make another identity the active one. A full reload rather than a re-render:
   *  the store, the roster decision and every open socket were all resolved for the
   *  identity that is being replaced. */
  async function switchTo(token: string) {
    setBusy(true)
    try {
      await fetch('/api/auth/multi-session/set-active', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionToken: token }),
      })
      location.assign('/home')
    } finally {
      setBusy(false)
    }
  }

  async function signOut() {
    await api.logout()
    location.assign('/')
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="rounded-control flex h-7 items-center gap-1 px-1.5 hover:bg-[var(--rail-hover)]"
        style={{ color: 'var(--muted)' }}
        aria-haspopup="menu"
        aria-expanded={open}
        title={me?.name ?? 'Account'}
        aria-label="Account"
      >
        <Icon name="User" size={14} />
        <Icon name="ChevronDown" size={12} />
      </button>
      {open ? (
        <div
          role="menu"
          className="rounded-control absolute right-0 z-40 mt-1 w-[17rem] border py-1 shadow-lg"
          style={{ background: 'var(--panel)', borderColor: 'var(--border)' }}
        >
          <div className="flex items-start gap-2 px-3 py-1.5">
            <span className="mt-0.5 w-3.5 shrink-0">
              <Icon name="Check" size={14} />
            </span>
            <span className="min-w-0">
              <p className="text-body truncate font-medium">{me?.name}</p>
              {info?.me.email ? (
                <p
                  className="num truncate"
                  style={{ fontSize: 'var(--text-label)', color: 'var(--muted)' }}
                >
                  {info.me.email}
                </p>
              ) : (
                // A demo guest has a name and no address, and saying so is honest:
                // nothing they do here is attached to an account.
                <p className="meta">signed in as a guest</p>
              )}
            </span>
          </div>

          {others.length > 0 ? (
            <>
              <div className="my-1 h-px" style={{ background: 'var(--border)' }} />
              <p className="eyebrow px-3 pt-1 pb-0.5">Also signed in</p>
              {others.map((s) => (
                <button
                  key={s.token}
                  type="button"
                  role="menuitem"
                  disabled={busy}
                  onClick={() => void switchTo(s.token)}
                  className="flex w-full items-start gap-2 px-3 py-1.5 text-left hover:bg-[var(--rail-hover)] disabled:opacity-50"
                >
                  <span className="w-3.5 shrink-0" />
                  <span className="min-w-0">
                    <p className="text-body truncate">{s.name || s.email}</p>
                    {s.email ? (
                      <p
                        className="num truncate"
                        style={{ fontSize: 'var(--text-label)', color: 'var(--muted)' }}
                      >
                        {s.email}
                      </p>
                    ) : null}
                  </span>
                </button>
              ))}
            </>
          ) : null}

          <div className="my-1 h-px" style={{ background: 'var(--border)' }} />
          {/* Temporary, while the installed app's top bar is being chased down. */}
          <a
            href="/pwa-check"
            role="menuitem"
            className="text-body block px-3 py-1.5 hover:bg-[var(--rail-hover)]"
          >
            PWA diagnostics
          </a>
          <button
            type="button"
            role="menuitem"
            onClick={() => void signOut()}
            className="text-body block w-full px-3 py-1.5 text-left hover:bg-[var(--rail-hover)]"
          >
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  )
}
